import * as R from 'ramda'
import * as regexp from './regexp'
import {AllHtmlEntities} from 'html-entities'

// prettier-ignore
const BLOCK_TAGS = [
  'address', 'article', 'aside', 'blockquote', 'canvas', 'dd', 'div', 'dl',
  'dt', 'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2',
  'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'li', 'main', 'nav', 'noscript',
  'ol', 'p', 'pre', 'section', 'table', 'tfoot', 'ul', 'video',
]
const SCRIPT_TAGS = ['script', 'style']
const ENTITIES = new AllHtmlEntities()

/**
 * Make a tag stripper. This is not a security mechanism, it's purely
 * a convenience for avoiding unwanted tags or entities in rendered text.
 */
export const tagStripper = ({
  scriptTags = SCRIPT_TAGS,
  blockTags = BLOCK_TAGS,
  allowTags = [],
  allowAttrs = [],
  decodeEntities = true,
} = {}) => {
  const scriptTagsRe = regexp.tag('gisux')`
    <
    (${scriptTags.join('|')})\b
    [^>]*>
    (?:.*?</\\1\s*>)?`
  const blockTagsRe = regexp.tag('gisux')`
    </?
    (${blockTags.join('|')})\b
    [^>]*>`
  const otherTagsRe = regexp.tag('gisux')`
    </?
    (\w[^>\s]*)
    [^>]*>`
  const attrTagsRe = regexp.tag('gisux')`
    <\w[^>]*>`
  const attrsRe = regexp.tag('gisux')`
    \s+
    ([^=\s]+)=(?:"[^"]*"|'[^']*'|\S*)`
  const except = (allow, r) => (match, p1) =>
    p1 && allow && allow.includes(p1) ? match : r
  return R.pipe(
    // Kill script/style tags and contents
    R.replace(scriptTagsRe, except(allowTags, '')),
    // Kill block tags, preserving contents, adding space
    R.replace(blockTagsRe, except(allowTags, ' ')),
    // Kill other tags, preserving contents
    R.replace(otherTagsRe, except(allowTags, '')),
    // If any tags allowed, kill attrs
    R.replace(attrTagsRe, R.replace(attrsRe, except(allowAttrs, ''))),
    // Decode HTML entities
    decodeEntities ? ENTITIES.decode : R.identity,
  )
}

/**
 * Strip all tags, for situations where we get tags unexpectedly in text.
 */
export const stripTags = tagStripper()

/**
 * Simplify HTML for constrained display, such as sub-heading in the Header
 * block, or content details. There shouldn't be linebreaks or other block
 * elements here, but we do allow simple formatting and links.
 */
export const simplifyHtml = tagStripper({
  allowTags: ['a', 'b', 'em', 'i', 'strong', 'sub', 'sup'],
  allowAttrs: ['href', 'target'],
  // Decoding entities is only needed when displaying as text, not when
  // displaying as HTML because then the browser will do the decode.
  decodeEntities: false,
})

/**
 * Simplify HTML for TOC.
 */
export const simplifyTocHtml = R.pipe(
  R.replace(regexp.tag('gisux')`<br[^>]*>`, ' | '),
  tagStripper({
    allowTags: ['b', 'em', 'i', 'strong', 'sub', 'sup'],
    // Decoding entities is only needed when displaying as text, not when
    // displaying as HTML because then the browser will do the decode.
    decodeEntities: false,
  }),
)

export const normalizeSpace = s => s.trim().replace(/\s+/, ' ')
